import React, { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { nlPost } from '../../apis/notLoginApi';

const TabButton = memo(({ id, label, selected, onClick }) => (
  <li
    id={`tab_login_${id}`}
    role="tab"
    aria-selected={selected}
    aria-controls={`panel_login_${id}`}
    className={selected ? 'active' : ''}
  >
    <button type="button" className="btn-tab" onClick={() => onClick(id)}>
      {label} {selected && <i className="sr-only"> 선택됨</i>}
    </button>
  </li>
));

const InputField = memo(({ id, label, value, type = 'text', onChange }) => (
  <div className="form-group">
    <div className="form-tit">
      <label htmlFor={id}>{label}</label>
    </div>
    <div className="form-conts">
      <input
        type={type}
        id={id}
        className="form-control"
        value={value}
        onChange={onChange}
        aria-labelledby="userId"
      />
    </div>
  </div>
));

const TabContent = memo(
  ({ type, fields, userData, onChange, onFindPwClick }) => (
    <section
      id={`panel_login_${type}`}
      role="tabpanel"
      aria-labelledby={`tab_login_${type}`}
      className="tab-conts active"
    >
      <h3 className="sr-only">
        {type === 'user' ? '사용자' : '기업 사용자'} 비밀번호 찾기
      </h3>
      <p className="text-gray-70">
        아이디, 성명, 이메일 주소를 입력하신 후 비밀번호 찾기를 누르시면 정보가
        일치할 경우 이메일로 임시 비밀번호를 발급받으실 수 있습니다.
      </p>
      <div className="login-form-area type-tab">
        <div className="login-wrap">
          <div className="in">
            <div className="fieldset">
              <legend>비밀번호 찾기 폼</legend>
              {fields.map(({ id, label, type }) => (
                <>
                  <label className="hidden-label" htmlFor={id}>
                    라벨
                  </label>
                  <InputField
                    key={id}
                    id={id}
                    label={label}
                    type={type}
                    value={userData[id]}
                    onChange={onChange}
                    aria-labelledby={id}
                  />
                </>
              ))}
              <div className="form-group">
                <button
                  type="button"
                  className="btn primary"
                  onClick={onFindPwClick}
                >
                  비밀번호 찾기
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
);

const Modal = memo(({ show, message, onClose, onGoToLogin }) =>
  show ? (
    <div
      className="modal fade in shown"
      role="dialog"
      aria-labelledby="tit_search_pwd"
      aria-hidden={!show}
    >
      <div className="modal-dialog sm">
        <div className="modal-content">
          <div className="modal-header">
            <h2 id="tit_search_pwd" className="modal-title">
              비밀번호 찾기 결과
            </h2>
          </div>
          <div className="modal-conts">{message}</div>
          <div className="modal-btn btn-wrap both">
            <button type="button" className="btn md tertiary" onClick={onClose}>
              닫기
            </button>
            <button
              type="button"
              className="btn md primary"
              onClick={onGoToLogin}
            >
              로그인 하기
            </button>
          </div>
          <button
            type="button"
            className="btn-close close-modal"
            onClick={onClose}
          >
            <span className="sr-only">닫기</span>
          </button>
        </div>
      </div>
      <div className="modal-back in" onClick={onClose}></div>
    </div>
  ) : null
);

const FindPassword = () => {
  useEffect(() => {
    document.title = '비밀번호 찾기';
  }, []);
  const [activeTab, setActiveTab] = useState('user');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [userData, setUserData] = useState({ id: '', name: '', email: '' });

  const navigate = useNavigate();

  const handleTabClick = tab => {
    setActiveTab(tab);
    setUserData({ id: '', name: '', email: '' });
  };

  const handleInputChange = ({ target: { id, value } }) => {
    setUserData(prev => ({ ...prev, [id]: value }));
  };

  const handleFindPwClick = async () => {
    const url = '/member/recoverPassword.do';
    const tempParam = {
      id: userData.id,
      name: userData.name,
      email: userData.email,
      memberType: activeTab,
    };

    try {
      const response = await nlPost(url, tempParam);
      let message;

      if (response instanceof Error || response.code === undefined) {
        message = (
          <p>
            <strong>실패:</strong> 알 수 없는 오류가 발생했습니다.
          </p>
        );
      } else {
        switch (response.code) {
          case 1:
            message = (
              <div className="txt-box center">
                <p className="text-gray-90">
                  사용자님의 등록된 메일 주소로 <br />
                  임시 비밀번호를 전송하였습니다.
                </p>
                <p className="text-gray-90 gap-sm">
                  등록하신 이메일: <strong>{userData.email}</strong>
                </p>
                <p className="text-secondary-60 gap-sm">
                  <strong>로그인 후 반드시 비밀번호를 변경해주세요.</strong>
                </p>
              </div>
            );
            break;
          case 2:
            message = <p className="text-gray-90">등록된 회원이 없습니다.</p>;
            break;
          default:
            message = (
              <p className="text-gray-90">알 수 없는 오류가 발생했습니다.</p>
            );
        }
      }
      setModalMessage(message);
      setShowModal(true);
    } catch {
      setModalMessage(
        <p className="text-gray-90">알 수 없는 오류가 발생했습니다.</p>
      );
      setShowModal(true);
    }
  };

  const closeModal = () => setShowModal(false);
  const goToLogin = () => navigate('/member/login');

  return (
    <>
      <div className="page-title-wrap" data-type="responsive">
        <h2 className="h-tit">비밀번호 찾기</h2>
      </div>

      <div className="tab-area layer">
        <div className="tab line full">
          <ul role="tablist">
            <TabButton
              id="user"
              label="사용자 비밀번호 찾기"
              selected={activeTab === 'user'}
              onClick={handleTabClick}
            />
            <TabButton
              id="ent"
              label="기업 사용자 비밀번호 찾기"
              selected={activeTab === 'ent'}
              onClick={handleTabClick}
            />
          </ul>
        </div>

        <div className="tab-conts-wrap">
          {activeTab === 'user' && (
            <TabContent
              type="user"
              fields={[
                { id: 'id', label: '아이디' },
                { id: 'name', label: '성명' },
                { id: 'email', label: '이메일', type: 'email' },
              ]}
              userData={userData}
              onChange={handleInputChange}
              onFindPwClick={handleFindPwClick}
            />
          )}
          {activeTab === 'ent' && (
            <TabContent
              type="ent"
              fields={[
                { id: 'id', label: '아이디' },
                { id: 'name', label: '담당자 성명' },
                { id: 'email', label: '담당자 이메일', type: 'email' },
              ]}
              userData={userData}
              onChange={handleInputChange}
              onFindPwClick={handleFindPwClick}
            />
          )}
        </div>
      </div>

      <Modal
        show={showModal}
        message={modalMessage}
        onClose={closeModal}
        onGoToLogin={goToLogin}
      />
    </>
  );
};

export default FindPassword;
